import { useId, useState, type ReactNode } from "react";
import { CHAT_MAX_FOLLOWUPS } from "../constants";
import { markdownToPlainText, renderMarkdown } from "../markdown";
import type { ChatMessage, ChatStage, FeedbackInput } from "../types";
import { isScriptedWorkflowMessage } from "../workflow/step";
import { ActionCard, type ActionHandlers } from "./action-card";
import { ArticleCard } from "./article-card";
import { BotBubble, type ChatAudience } from "./bubbles";
import { FollowUps } from "./chat-log";
import { CitationChip, SourcesList, snippetId } from "./citations";
import { FeedbackBar, type FeedbackBarProps } from "./feedback-bar";

export type { ActionHandlers };

/** A new offer on the same message (different run) remounts its card. */
function actionKey(action: NonNullable<ChatMessage["action"]>): string {
  if (action.type === "workflow_step") return `${action.type}:${action.payload.run_id}:${action.payload.step_id}`;
  return action.type === "book_tour" || action.type === "lead_form" ? `${action.type}:${action.payload.run_id ?? action.payload.action_id}` : action.type;
}

/** The server removes `[n]` markers unless the agent opted into "Inline [1]"; chip numbers follow. */
const INLINE_MARKER = /\[\d{1,3}\]/;

export interface AssistantMessageProps extends ActionHandlers {
  message: ChatMessage;
  botName: string;
  avatar?: ReactNode;
  onAsk: (text: string) => void;
  onFeedback?: (messageId: string, input: FeedbackInput) => void | Promise<void>;
  feedbackVariant?: FeedbackBarProps["variant"];
  /** Staff audience only: wording per retrieval stage. */
  stageLabels?: Partial<Record<ChatStage, string>>;
  /** "visitor" (default) shows a neutral rotating status; "staff" shows the stage wording (see `ChatAudience`). */
  audience?: ChatAudience;
  /** Visitor audience: the localized neutral rotation (defaults to English). */
  statusPhrases?: readonly string[];
  /** Widget variant: score / updated date / open link in the snippet. */
  detailedSources?: boolean;
  /** Disables follow-ups while another answer streams. */
  busy?: boolean;
  /** Visible label above the follow-up chips. */
  relatedLabel?: string;
}

/** Bot answer + after-area: sources → action card → follow-ups → feedback (spec §5.3 order). */
export function AssistantMessage({ message, botName, avatar, onAsk, onFeedback, feedbackVariant, stageLabels, audience, statusPhrases, detailedSources, busy, relatedLabel = "Related questions", ...handlers }: AssistantMessageProps) {
  const baseId = useId();
  const panelId = snippetId(baseId);
  const textId = `${baseId}-text`;
  const [active, setActive] = useState<number | null>(null);
  const toggle = (n: number) => {
    setActive((current) => (current === n ? null : n));
  };
  const streaming = message.status === "streaming";
  const waiting = streaming && message.text.length === 0;
  const finished = message.status === "done";
  // A workflow turn is a scripted question, not an answer to rate or follow up on.
  const scripted = isScriptedWorkflowMessage(message);
  const citationByN = new Map(message.citations.map((c) => [c.n, c]));

  const body = renderMarkdown(message.text, {
    renderCitation: (n, key) => <CitationChip key={key} n={n} citation={citationByN.get(n)} active={active === n} controls={panelId} onToggle={toggle} />,
  });

  const after = finished ? (
    <>
      <SourcesList citations={message.citations} active={active} onToggle={toggle} snippetPanelId={panelId} detailed={detailedSources} showNumbers={INLINE_MARKER.test(message.text)} />
      {message.articles?.map((card) => <ArticleCard key={`${card.run_id}:${card.step_id}`} card={card} labels={handlers.workflowLabels} />)}
      {message.action ? <ActionCard key={actionKey(message.action)} action={message.action} handlers={{ ...handlers, textId, workflowBusy: busy }} messageId={message.id} /> : null}
      {scripted ? null : <FollowUps items={message.followups} onAsk={onAsk} disabled={busy} heading={relatedLabel} max={CHAT_MAX_FOLLOWUPS} />}
      {onFeedback && !scripted ? (
        <div className={message.followups.length ? "mt-3" : undefined}>
          <FeedbackBar
            copyText={markdownToPlainText(message.text)}
            gap={message.gap}
            variant={feedbackVariant}
            onFeedback={(input) => onFeedback(message.serverId ?? message.id, input)}
          />
        </div>
      ) : null}
    </>
  ) : null;

  return (
    <BotBubble name={botName} avatar={avatar} waiting={waiting} streaming={streaming} stage={message.stage} stageLabels={stageLabels} audience={audience} statusPhrases={statusPhrases} after={after} textId={textId}>
      {body}
    </BotBubble>
  );
}
